import { isCueDigit, timing, variantForLevel, } from './params';
/** Hash LCG déterministe à partir d'un seed entier. */
function lcg(seed) {
    const s = (Math.imul(seed | 0, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
}
/**
 * Tire le digit affiché : on cherche un digit cohérent avec le flag
 * isPmCue cible (cue ou pas) et la règle courante. Si la cible est cue,
 * on tire dans l'ensemble cue ; sinon dans le complément.
 */
function pickDigit(seed, wantCue, variant) {
    const candidates = [];
    for (let d = 0; d <= 9; d += 1) {
        const isCue = isCueDigit(d, variant.cueRule);
        if (wantCue && isCue)
            candidates.push(d);
        if (!wantCue && !isCue)
            candidates.push(d);
    }
    if (candidates.length === 0) {
        // Edge case (ne devrait jamais arriver) : retourne 0 par défaut.
        return 0;
    }
    const r = lcg(seed);
    return candidates[Math.floor(r * candidates.length)];
}
/**
 * Décide si le trial courant est un PM cue d'après le pmCueRatio.
 * Indépendant du digit pour respecter la fréquence cible.
 */
function trialIsPmCue(trialIdx, variant) {
    const r = lcg(trialIdx * 41 + 7);
    return r < variant.pmCueRatio;
}
export function makeStimulus(trialIdx, isiMs, level) {
    const variant = variantForLevel(level);
    const isPmCue = trialIsPmCue(trialIdx, variant);
    const digit = pickDigit(trialIdx * 13 + 11, isPmCue, variant);
    // Digit déclencheur de la PM : le premier digit "cue" par la règle
    // (utilisé pour l'affichage de la consigne dans l'UI).
    let pmCueDigit = 7;
    for (let d = 0; d <= 9; d += 1) {
        if (isCueDigit(d, variant.cueRule)) {
            pmCueDigit = d;
            break;
        }
    }
    return {
        trialIdx,
        isiMs: Math.max(300, isiMs),
        digit,
        isPmCue,
        pmCueDigit,
    };
}
function gradeAnswer(answer, stim) {
    if (stim.isPmCue)
        return answer === 'pm';
    if (answer === 'pm')
        return false;
    const expected = stim.digit % 2 === 0 ? 'even' : 'odd';
    return answer === expected;
}
export function createProspectiveMemoryAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(answer, stim) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const correct = gradeAnswer(answer, stim);
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ answer, reactionTimeMs: rt, correct });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        timers.push(setTimeout(() => {
                            if (!resolver)
                                return;
                            const r = resolver;
                            resolver = null;
                            rejecter = null;
                            // Pas de réponse : on prend la mauvaise parité par défaut
                            // (incorrect quel que soit le cas).
                            const fallback = stim.digit % 2 === 0 ? 'odd' : 'even';
                            r({
                                answer: fallback,
                                reactionTimeMs: timing.responseWindowMs,
                                correct: false,
                            });
                        }, timing.responseWindowMs));
                    }, stim.isiMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleAnswer(answer) {
            if (state.subPhase !== 'awaiting_input' &&
                state.subPhase !== 'stimulus') {
                return;
            }
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(answer, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map